import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  Alert,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Backdrop,
  CircularProgress,
  TextField,
  IconButton,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Assignment as OrderIcon,
  PersonalInjury as PatientIcon,
  MedicalServices as PhysicianIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  AutoFixHigh as AutoFixIcon,
  Search as SearchIcon,
  OpenInNew as OpenInNewIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { orderService } from "../services/orderService";
import { efaxService } from "../services/efaxService";
import { useNavigate } from "react-router-dom";

const EFaxUploadModal = ({ open, onClose }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [processingStep, setProcessingStep] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [requiresManualEntry, setRequiresManualEntry] = useState(false);
  const [validationStatus, setValidationStatus] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  // Edit form state
  const [editData, setEditData] = useState({
    patient: {},
    physician: {},
    order: {},
    diagnoses: [],
    exams: [],
  });

  // Form validation errors
  const [validationErrors, setValidationErrors] = useState({});

  // Loading states
  const [saving, setSaving] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Autocomplete options
  const [physicianOptions, setPhysicianOptions] = useState([]);
  const [diagnosisOptions, setDiagnosisOptions] = useState([]);
  const [examOptions, setExamOptions] = useState([]);

  // FIXED: In-memory session storage instead of localStorage
  const [sessionData, setSessionData] = useState({
    savedFormData: null,
    localPhysicians: [],
    localOrders: [],
  });

  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      resetState();
      // Auto-open file picker
      setTimeout(() => {
        if (fileInputRef.current) {
          fileInputRef.current.click();
        }
      }, 100);
    }
  }, [open]);

  // Load options when entering edit mode
  useEffect(() => {
    if (editMode) {
      loadEditOptions();
    }
  }, [editMode]);

  const resetState = () => {
    setFile(null);
    setUploading(false);
    setUploadProgress(0);
    setError("");
    setSuccess(false);
    setParsedData(null);
    setCreatedOrder(null);
    setProcessingStep("");
    setEditMode(false);
    setRequiresManualEntry(false);
    setValidationStatus(null);
    setActiveStep(0);
    setEditData({
      patient: {},
      physician: {},
      order: {},
      diagnoses: [],
      exams: [],
    });
    setValidationErrors({});
    // FIXED: Clear session data instead of localStorage
    setSessionData({
      savedFormData: null,
      localPhysicians: [],
      localOrders: [],
    });
  };

  const loadEditOptions = async () => {
    setLoadingOptions(true);
    try {
      const [physicians, diagnoses, exams] = await Promise.all([
        orderService.getPhysicians(),
        orderService.getDiagnoses(),
        orderService.getExamTypes(),
      ]);
      setPhysicianOptions(physicians);
      setDiagnosisOptions(diagnoses);
      setExamOptions(exams);
    } catch (error) {
      console.error("Error loading options:", error);
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setError("");

      // Auto-start processing
      await processFile(selectedFile);
    }
  };

  const processFile = async (fileToUpload) => {
    if (!fileToUpload) {
      setError("No file selected");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress with steps
      const steps = [
        { step: "Uploading file...", progress: 20 },
        { step: "Scanning document...", progress: 40 },
        { step: "Extracting patient information...", progress: 60 },
        { step: "Processing order details...", progress: 80 },
        { step: "Validating data...", progress: 95 },
        { step: "Finalizing...", progress: 100 },
      ];

      let currentStep = 0;
      const progressInterval = setInterval(() => {
        if (currentStep < steps.length) {
          setProcessingStep(steps[currentStep].step);
          setUploadProgress(steps[currentStep].progress);
          currentStep++;
        } else {
          clearInterval(progressInterval);
        }
      }, 800);

      // Create form data
      const formData = new FormData();
      formData.append("file", fileToUpload);

      // Upload the file
      const response = await efaxService.uploadEFax(formData);

      clearInterval(progressInterval);
      setUploadProgress(100);
      setProcessingStep("Complete!");

      // Check if manual entry is required
      if (
        response.requires_manual_entry ||
        response.parsed_data?.requires_manual_entry
      ) {
        setRequiresManualEntry(true);
        setError(
          response.parsed_data?.error ||
            response.error ||
            "Document parsing requires manual review"
        );

        // Set up manual entry with defaults
        const defaultData = {
          patient: {
            first_name: "",
            last_name: "",
            date_of_birth: "",
            gender: "",
            phone_number: "",
            email: "",
            address: "",
            city: "",
            state: "CA",
            zip_code: "",
          },
          physician: {
            first_name: "",
            last_name: "",
            npi: "",
            facility_name: "",
          },
          order: {
            place_of_service: "Home",
            ordering_health_company: "",
            special_notes: "Manual entry required - please review document",
          },
          diagnoses: [],
          exams: [],
        };

        // Merge any parsed data with defaults
        if (response.parsed_data) {
          setParsedData(response.parsed_data);
          setEditData({
            patient: {
              ...defaultData.patient,
              ...response.parsed_data.patient,
            },
            physician: {
              ...defaultData.physician,
              ...response.parsed_data.physician,
            },
            order: { ...defaultData.order, ...response.parsed_data.order },
            diagnoses: response.parsed_data.diagnoses || [],
            exams: response.parsed_data.exams || [],
          });
        } else {
          setEditData(defaultData);
        }

        setEditMode(true);
        setActiveStep(1); // Move to validation step
        return;
      }

      // Process successful response
      if (response.parsed_data) {
        setParsedData(response.parsed_data);
        setValidationStatus(response.parsed_data.validation);

        // Initialize edit data with parsed data
        setEditData({
          patient: response.parsed_data.patient || {},
          physician: response.parsed_data.physician || {},
          order: response.parsed_data.order || {},
          diagnoses: response.parsed_data.diagnoses || [],
          exams: response.parsed_data.exams || [],
        });

        // Check if validation failed or requires review
        if (
          response.parsed_data.validation &&
          !response.parsed_data.validation.is_valid
        ) {
          setEditMode(true);
          setActiveStep(1); // Move to validation step
          setError(
            "Some required information is missing. Please review and complete the form."
          );
        } else {
          setActiveStep(2); // Move to success step
        }
      }

      // Handle different response structures
      if (response.order) {
        setCreatedOrder(response.order);
        setSuccess(true);
      } else if (response.order_id) {
        // Fallback: create order object from top-level response properties
        setCreatedOrder({
          id: response.order_id,
          order_number: response.order_number,
          message: response.message,
        });
        setSuccess(true);
      }
    } catch (err) {
      console.error("Error uploading eFax:", err);
      setError(
        err.response?.data?.detail || "Error uploading and processing eFax"
      );
      setRequiresManualEntry(true);
      setEditMode(true);
      setActiveStep(1);
    } finally {
      setUploading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    // Validate required patient fields
    if (!editData.patient.first_name?.trim()) {
      errors["patient.first_name"] = "First name is required";
    }
    if (!editData.patient.last_name?.trim()) {
      errors["patient.last_name"] = "Last name is required";
    }
    if (
      !editData.patient.address?.trim() ||
      editData.patient.address === "Address not provided"
    ) {
      errors["patient.address"] = "Address is required";
    }
    if (!editData.patient.city?.trim() || editData.patient.city === "Unknown") {
      errors["patient.city"] = "City is required";
    }
    if (!editData.patient.state?.trim()) {
      errors["patient.state"] = "State is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (section, field, value) => {
    setEditData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));

    // Clear validation error for this field
    const errorKey = `${section}.${field}`;
    if (validationErrors[errorKey]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleDiagnosisChange = (index, field, value) => {
    setEditData((prev) => ({
      ...prev,
      diagnoses: prev.diagnoses.map((diag, i) =>
        i === index ? { ...diag, [field]: value } : diag
      ),
    }));
  };

  const handleExamChange = (index, field, value) => {
    setEditData((prev) => ({
      ...prev,
      exams: prev.exams.map((exam, i) =>
        i === index ? { ...exam, [field]: value } : exam
      ),
    }));
  };

  const addDiagnosis = () => {
    setEditData((prev) => ({
      ...prev,
      diagnoses: [
        ...prev.diagnoses,
        { code: "", description: "", primary: false },
      ],
    }));
  };

  const removeDiagnosis = (index) => {
    setEditData((prev) => ({
      ...prev,
      diagnoses: prev.diagnoses.filter((_, i) => i !== index),
    }));
  };

  const addExam = () => {
    setEditData((prev) => ({
      ...prev,
      exams: [...prev.exams, { name: "", description: "" }],
    }));
  };

  const removeExam = (index) => {
    setEditData((prev) => ({
      ...prev,
      exams: prev.exams.filter((_, i) => i !== index),
    }));
  };

  const saveChanges = async () => {
    if (!validateForm()) {
      setError("Please fill in all required fields marked in red.");
      return;
    }

    setSaving(true);
    try {
      if (requiresManualEntry) {
        // Create new order with manual data using the new endpoint
        const orderData = {
          patient_data: editData.patient,
          physician_data: editData.physician,
          order_data: editData.order,
          diagnoses: editData.diagnoses,
          exams: editData.exams,
        };

        const response = await efaxService.createManualOrder(orderData);
        setCreatedOrder(response);
        setSuccess(true);
        setActiveStep(2);
      } else if (createdOrder?.id) {
        // Update existing order with corrected information
        const updateData = {
          patient_data: editData.patient,
          physician_data: editData.physician,
          order_data: editData.order,
          diagnoses: editData.diagnoses,
          exams: editData.exams,
        };

        const response = await efaxService.updateOrderFromEFax(
          createdOrder.id,
          updateData
        );

        setCreatedOrder(response);
        setSuccess(true);
        setActiveStep(2);
      }

      setParsedData(editData);
      setEditMode(false);
      setError("");
    } catch (error) {
      console.error("Error saving changes:", error);
      setError(`Failed to save changes: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!uploading && !saving) {
      onClose();
    }
  };

  const viewOrderDetails = () => {
    if (createdOrder && createdOrder.id) {
      navigate(`/orders/${createdOrder.id}`);
      onClose();
    }
  };

  const uploadAnotherFile = () => {
    resetState();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };


  const getValidationIcon = (field) => {
    const errorKey = `patient.${field}`;
    if (validationErrors[errorKey]) {
      return <ErrorIcon color="error" fontSize="small" />;
    }
    if (
      editData.patient[field] &&
      editData.patient[field] !== "Unknown" &&
      editData.patient[field] !== "Address not provided"
    ) {
      return <CheckIcon color="success" fontSize="small" />;
    }
    return <WarningIcon color="warning" fontSize="small" />;
  };

  // Function to open NPI registry with search
  const openNPIRegistry = () => {
    const physicianName = `${editData.physician.first_name || ""} ${
      editData.physician.last_name || ""
    }`.trim();
    const searchParams = physicianName
      ? `?version=2.1&number=&taxonomy_description=&name=${encodeURIComponent(
          physicianName
        )}`
      : "";

    window.open(`https://npiregistry.cms.hhs.gov/${searchParams}`, "_blank");
  };

  const renderValidationSummary = () => {
    if (!validationStatus) return null;

    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <AutoFixIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="subtitle1">Data Extraction Results</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid  size={{ xs: 12, sm: 4 }}>
              <Typography variant="body2" color="textSecondary">
                Completeness
              </Typography>
              <Box display="flex" alignItems="center">
                <LinearProgress
                  variant="determinate"
                  value={validationStatus.completeness_score}
                  sx={{ flexGrow: 1, mr: 1 }}
                />
                <Typography variant="body2">
                  {validationStatus.completeness_score}%
                </Typography>
              </Box>
            </Grid>

            <Grid  size={{ xs: 12, sm: 4 }}>
              <Typography variant="body2" color="textSecondary">
                AI Confidence
              </Typography>
              <Box display="flex" alignItems="center">
                <LinearProgress
                  variant="determinate"
                  value={(validationStatus.confidence_score || 0) * 100}
                  color={validationStatus.confidence_score > 0.7 ? "success" : validationStatus.confidence_score > 0.4 ? "warning" : "error"}
                  sx={{ flexGrow: 1, mr: 1 }}
                />
                <Typography variant="body2">
                  {((validationStatus.confidence_score || 0) * 100).toFixed(0)}%
                </Typography>
              </Box>
            </Grid>

            <Grid  size={{ xs: 12, sm: 4 }}>
              <Typography variant="body2" color="textSecondary">
                Status
              </Typography>
              <Chip
                label={
                  validationStatus.is_valid
                    ? "Ready to Process"
                    : "Requires Review"
                }
                color={validationStatus.is_valid ? "success" : "warning"}
                size="small"
              />
            </Grid>

            {validationStatus.missing_required?.length > 0 && (
              <Grid  size={{ xs: 12 }}>
                <Typography variant="body2" color="error" gutterBottom>
                  Missing Required Fields:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {validationStatus.missing_required.map((field) => (
                    <Chip
                      key={field}
                      label={field.replace("patient.", "").replace("_", " ")}
                      color="error"
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderPatientSection = () => {
    const patient = editData.patient || {};

    return (
      <Card variant="outlined">
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <PatientIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="subtitle1">Patient Information</Typography>
            <Typography
              variant="caption"
              sx={{ ml: 1, color: "text.secondary" }}
            >
              * Required fields
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="First Name *"
                value={patient.first_name || ""}
                onChange={(e) =>
                  handleInputChange("patient", "first_name", e.target.value)
                }
                error={!!validationErrors["patient.first_name"]}
                helperText={validationErrors["patient.first_name"]}
                InputProps={{
                  endAdornment: getValidationIcon("first_name"),
                }}
              />
            </Grid>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Last Name *"
                value={patient.last_name || ""}
                onChange={(e) =>
                  handleInputChange("patient", "last_name", e.target.value)
                }
                error={!!validationErrors["patient.last_name"]}
                helperText={validationErrors["patient.last_name"]}
                InputProps={{
                  endAdornment: getValidationIcon("last_name"),
                }}
              />
            </Grid>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                value={patient.date_of_birth || ""}
                onChange={(e) =>
                  handleInputChange("patient", "date_of_birth", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={patient.gender || ""}
                  label="Gender"
                  onChange={(e) =>
                    handleInputChange("patient", "gender", e.target.value)
                  }
                >
                  <MenuItem value="">Select Gender</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={patient.phone_number || ""}
                onChange={(e) =>
                  handleInputChange("patient", "phone_number", e.target.value)
                }
                placeholder="(555) 123-4567"
              />
            </Grid>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={patient.email || ""}
                onChange={(e) =>
                  handleInputChange("patient", "email", e.target.value)
                }
              />
            </Grid>
            <Grid  size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Address *"
                value={patient.address || ""}
                onChange={(e) =>
                  handleInputChange("patient", "address", e.target.value)
                }
                error={!!validationErrors["patient.address"]}
                helperText={validationErrors["patient.address"]}
                InputProps={{
                  endAdornment: getValidationIcon("address"),
                }}
              />
            </Grid>
            <Grid  size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="City *"
                value={patient.city || ""}
                onChange={(e) =>
                  handleInputChange("patient", "city", e.target.value)
                }
                error={!!validationErrors["patient.city"]}
                helperText={validationErrors["patient.city"]}
                InputProps={{
                  endAdornment: getValidationIcon("city"),
                }}
              />
            </Grid>
            <Grid  size={{ xs: 12, sm: 4 }}>
              <FormControl
                fullWidth
                error={!!validationErrors["patient.state"]}
              >
                <InputLabel>State *</InputLabel>
                <Select
                  value={patient.state || "CA"}
                  label="State *"
                  onChange={(e) =>
                    handleInputChange("patient", "state", e.target.value)
                  }
                >
                  <MenuItem value="">Select State</MenuItem>
                  <MenuItem value="AL">Alabama</MenuItem>
                  <MenuItem value="AK">Alaska</MenuItem>
                  <MenuItem value="AZ">Arizona</MenuItem>
                  <MenuItem value="AR">Arkansas</MenuItem>
                  <MenuItem value="CA">California</MenuItem>
                  <MenuItem value="CO">Colorado</MenuItem>
                  <MenuItem value="CT">Connecticut</MenuItem>
                  <MenuItem value="DE">Delaware</MenuItem>
                  <MenuItem value="DC">District of Columbia</MenuItem>
                  <MenuItem value="FL">Florida</MenuItem>
                  <MenuItem value="GA">Georgia</MenuItem>
                  <MenuItem value="HI">Hawaii</MenuItem>
                  <MenuItem value="ID">Idaho</MenuItem>
                  <MenuItem value="IL">Illinois</MenuItem>
                  <MenuItem value="IN">Indiana</MenuItem>
                  <MenuItem value="IA">Iowa</MenuItem>
                  <MenuItem value="KS">Kansas</MenuItem>
                  <MenuItem value="KY">Kentucky</MenuItem>
                  <MenuItem value="LA">Louisiana</MenuItem>
                  <MenuItem value="ME">Maine</MenuItem>
                  <MenuItem value="MD">Maryland</MenuItem>
                  <MenuItem value="MA">Massachusetts</MenuItem>
                  <MenuItem value="MI">Michigan</MenuItem>
                  <MenuItem value="MN">Minnesota</MenuItem>
                  <MenuItem value="MS">Mississippi</MenuItem>
                  <MenuItem value="MO">Missouri</MenuItem>
                  <MenuItem value="MT">Montana</MenuItem>
                  <MenuItem value="NE">Nebraska</MenuItem>
                  <MenuItem value="NV">Nevada</MenuItem>
                  <MenuItem value="NH">New Hampshire</MenuItem>
                  <MenuItem value="NJ">New Jersey</MenuItem>
                  <MenuItem value="NM">New Mexico</MenuItem>
                  <MenuItem value="NY">New York</MenuItem>
                  <MenuItem value="NC">North Carolina</MenuItem>
                  <MenuItem value="ND">North Dakota</MenuItem>
                  <MenuItem value="OH">Ohio</MenuItem>
                  <MenuItem value="OK">Oklahoma</MenuItem>
                  <MenuItem value="OR">Oregon</MenuItem>
                  <MenuItem value="PA">Pennsylvania</MenuItem>
                  <MenuItem value="RI">Rhode Island</MenuItem>
                  <MenuItem value="SC">South Carolina</MenuItem>
                  <MenuItem value="SD">South Dakota</MenuItem>
                  <MenuItem value="TN">Tennessee</MenuItem>
                  <MenuItem value="TX">Texas</MenuItem>
                  <MenuItem value="UT">Utah</MenuItem>
                  <MenuItem value="VT">Vermont</MenuItem>
                  <MenuItem value="VA">Virginia</MenuItem>
                  <MenuItem value="WA">Washington</MenuItem>
                  <MenuItem value="WV">West Virginia</MenuItem>
                  <MenuItem value="WI">Wisconsin</MenuItem>
                  <MenuItem value="WY">Wyoming</MenuItem>
                </Select>
                {validationErrors["patient.state"] && (
                  <Typography variant="caption" color="error">
                    {validationErrors["patient.state"]}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid  size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={patient.zip_code || ""}
                onChange={(e) =>
                  handleInputChange("patient", "zip_code", e.target.value)
                }
                placeholder="12345"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderPhysicianSection = () => {
    const physician = editData.physician || {};

    return (
      <Card variant="outlined">
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <PhysicianIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="subtitle1">Physician Information</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="First Name"
                value={physician.first_name || ""}
                onChange={(e) =>
                  handleInputChange("physician", "first_name", e.target.value)
                }
              />
            </Grid>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={physician.last_name || ""}
                onChange={(e) =>
                  handleInputChange("physician", "last_name", e.target.value)
                }
              />
            </Grid>
            <Grid  size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="NPI (National Provider Identifier)"
                value={physician.npi || ""}
                onChange={(e) =>
                  handleInputChange("physician", "npi", e.target.value)
                }
                placeholder="10-digit number (e.g., 1234567890)"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Search NPI Registry">
                        <IconButton
                          onClick={openNPIRegistry}
                          edge="end"
                          size="small"
                          sx={{ mr: 1 }}
                        >
                          <SearchIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="What is an NPI?">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() =>
                            window.open(
                              "https://npiregistry.cms.hhs.gov/",
                              "_blank"
                            )
                          }
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                helperText={
                  <Box
                    component="span"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <span>Need to find an NPI?</span>
                    <Link
                      href="https://npiregistry.cms.hhs.gov/"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      Search NPI Registry
                      <OpenInNewIcon fontSize="small" />
                    </Link>
                  </Box>
                }
              />
            </Grid>
            <Grid  size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Facility Name"
                value={physician.facility_name || ""}
                onChange={(e) =>
                  handleInputChange(
                    "physician",
                    "facility_name",
                    e.target.value
                  )
                }
              />
            </Grid>
          </Grid>

          {/* NPI Information Card */}
          <Card variant="outlined" sx={{ mt: 2, bgcolor: "grey.50" }}>
            <CardContent sx={{ py: 2 }}>
              <Box display="flex" alignItems="start" gap={1}>
                <InfoIcon color="info" fontSize="small" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    About NPI (National Provider Identifier)
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mb: 1 }}
                  >
                    The NPI is a unique 10-digit identification number for
                    healthcare providers in the United States.
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<SearchIcon />}
                      onClick={openNPIRegistry}
                    >
                      Search Registry
                    </Button>
                    <Link
                      href="https://npiregistry.cms.hhs.gov/"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: "0.875rem",
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      Visit NPI Registry
                      <OpenInNewIcon fontSize="small" />
                    </Link>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    );
  };

  const renderOrderSection = () => {
    const order = editData.order || {};
    const diagnoses = editData.diagnoses || [];
    const exams = editData.exams || [];

    return (
      <Card variant="outlined">
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <OrderIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="subtitle1">Order Details</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Place of Service</InputLabel>
                <Select
                  value={order.place_of_service || "Home"}
                  label="Place of Service"
                  onChange={(e) =>
                    handleInputChange(
                      "order",
                      "place_of_service",
                      e.target.value
                    )
                  }
                >
                  <MenuItem value="Home">Home</MenuItem>
                  <MenuItem value="Office">Office</MenuItem>
                  <MenuItem value="Hospital">Hospital</MenuItem>
                  <MenuItem value="Skilled Nursing Facility">
                    Skilled Nursing Facility
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Ordering Health Company"
                value={order.ordering_health_company || ""}
                onChange={(e) =>
                  handleInputChange(
                    "order",
                    "ordering_health_company",
                    e.target.value
                  )
                }
              />
            </Grid>
            <Grid  size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Special Notes"
                value={order.special_notes || ""}
                onChange={(e) =>
                  handleInputChange("order", "special_notes", e.target.value)
                }
              />
            </Grid>
          </Grid>

          {/* Diagnoses Section */}
          <Box sx={{ mt: 3 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Typography variant="subtitle2">Diagnoses</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addDiagnosis}
                size="small"
              >
                Add Diagnosis
              </Button>
            </Box>

            {diagnoses.map((diag, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid  size={{ xs: 12, sm: 3 }}>
                    <TextField
                      fullWidth
                      label="Code"
                      value={diag.code || ""}
                      onChange={(e) =>
                        handleDiagnosisChange(index, "code", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid  size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={diag.description || ""}
                      onChange={(e) =>
                        handleDiagnosisChange(
                          index,
                          "description",
                          e.target.value
                        )
                      }
                    />
                  </Grid>
                  <Grid  size={{ xs: 12, sm: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={diag.primary || false}
                          onChange={(e) =>
                            handleDiagnosisChange(
                              index,
                              "primary",
                              e.target.checked
                            )
                          }
                        />
                      }
                      label="Primary"
                    />
                  </Grid>
                  <Grid  size={{ xs: 12, sm: 1 }}>
                    <IconButton
                      onClick={() => removeDiagnosis(index)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}

            {diagnoses.length === 0 && (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontStyle: "italic" }}
              >
                No diagnoses added yet. Click "Add Diagnosis" to add one.
              </Typography>
            )}
          </Box>

          {/* Exams Section */}
          <Box sx={{ mt: 3 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Typography variant="subtitle2">Exams</Typography>
              <Button startIcon={<AddIcon />} onClick={addExam} size="small">
                Add Exam
              </Button>
            </Box>

            {exams.map((exam, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid  size={{ xs: 12, sm: 5 }}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={exam.name || ""}
                      onChange={(e) =>
                        handleExamChange(index, "name", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid  size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={exam.description || ""}
                      onChange={(e) =>
                        handleExamChange(index, "description", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid  size={{ xs: 12, sm: 1 }}>
                    <IconButton
                      onClick={() => removeExam(index)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}

            {exams.length === 0 && (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontStyle: "italic" }}
              >
                No exams added yet. Click "Add Exam" to add one.
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderStepperContent = () => {
    return (
      <Stepper activeStep={activeStep} orientation="vertical">
        <Step>
          <StepLabel>Upload Document</StepLabel>
          <StepContent>
            {!file && !uploading && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <input
                  accept=".pdf,.tiff,.tif"
                  style={{ display: "none" }}
                  id="raised-button-file"
                  type="file"
                  onChange={handleFileChange}
                  disabled={uploading}
                  ref={fileInputRef}
                />
                <UploadIcon
                  sx={{ fontSize: 60, color: "primary.main", mb: 2 }}
                />
                <Typography variant="h6" gutterBottom>
                  Select an eFax Document
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Supported formats: PDF, TIFF
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  htmlFor="raised-button-file"
                  startIcon={<UploadIcon />}
                  sx={{ mt: 2 }}
                >
                  Select File
                </Button>
              </Box>
            )}

            {file && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Processing: <strong>{file.name}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Size: {(file.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
            )}

            {uploading && (
              <Box sx={{ mt: 3, mb: 3 }}>
                <Typography variant="body2" gutterBottom>
                  {processingStep}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{ height: 8, borderRadius: 5 }}
                />
                <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                  {uploadProgress}% Complete
                </Typography>
              </Box>
            )}
          </StepContent>
        </Step>

        <Step>
          <StepLabel
            error={
              !!(validationErrors && Object.keys(validationErrors).length > 0)
            }
          >
            Review & Validate Data
          </StepLabel>
          <StepContent>
            {renderValidationSummary()}

            <Grid container spacing={3}>
              <Grid  size={{ xs: 12 }}>
                {renderPatientSection()}
              </Grid>
              <Grid  size={{ xs: 12, md: 6 }}>
                {renderPhysicianSection()}
              </Grid>
              <Grid  size={{ xs: 12, md: 6 }}>
                {renderOrderSection()}
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                onClick={saveChanges}
                disabled={saving}
                startIcon={<SaveIcon />}
              >
                {saving ? "Creating Order..." : "Create Order"}
              </Button>
              <Button onClick={uploadAnotherFile}>Upload Another File</Button>
            </Box>
          </StepContent>
        </Step>

        <Step>
          <StepLabel>Order Created</StepLabel>
          <StepContent>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Box display="flex" alignItems="center">
                <CheckIcon sx={{ mr: 1 }} />
                <Typography variant="body1">
                  Order <strong>{createdOrder?.order_number || "N/A"}</strong>{" "}
                  has been created successfully!
                </Typography>
              </Box>
            </Alert>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={viewOrderDetails}
                disabled={!createdOrder?.id}
              >
                View Order Details
              </Button>
              <Button onClick={uploadAnotherFile}>Upload Another File</Button>
            </Box>
          </StepContent>
        </Step>
      </Stepper>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="efax-upload-dialog-title"
    >
      <DialogTitle id="efax-upload-dialog-title">
        eFax Document Processing
      </DialogTitle>
      <DialogContent>
        {error && !editMode && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {error && editMode && (
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center">
                <WarningIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="subtitle2">
                  Document Processing Issues
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="warning">{error}</Alert>
              <Typography variant="body2" sx={{ mt: 2 }}>
                The document could not be automatically processed. Please review
                and complete the information below manually.
              </Typography>
            </AccordionDetails>
          </Accordion>
        )}

        {renderStepperContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={uploading || saving}>
          {success ? "Close" : "Cancel"}
        </Button>
      </DialogActions>

      {/* Processing backdrop */}
      <Backdrop
        open={uploading || saving}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" sx={{ mt: 2, color: "white" }}>
            {saving ? "Creating order..." : processingStep}
          </Typography>
        </Box>
      </Backdrop>
    </Dialog>
  );
};

export default EFaxUploadModal;
